/**
 * UDP Shared Helpers — single source of truth for utility functions.
 *
 * Used by both frontend and backend.
 *
 * @module udp-schema/helpers
 */

import { getFieldMeta, getFieldOptions } from './field-helpers.js';
import type { FieldOption } from './profile-fields/types.js';

/**
 * Calculate age from an ISO date string (YYYY-MM-DD or Date-parseable).
 *
 * @returns Age in years, or null if invalid.
 */
export function calcAge(dob: string | Date | undefined | null): number | null {
  if (!dob) return null;
  const d = typeof dob === 'string' ? new Date(dob) : dob;
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return age;
}

/**
 * Check if a field should be visible based on its `conditionalOn` rule.
 *
 * Looks up the field's metadata from profile-fields. If it has a `conditionalOn`
 * property, checks if the controlling field's current value is in the allowed list.
 *
 * @param fieldKey - The field to check visibility for
 * @param form - Current form data (flat key-value object)
 * @returns true if the field should be shown (no condition, or condition met)
 */
export function isFieldVisible(fieldKey: string, form: Record<string, unknown>): boolean {
  const meta = getFieldMeta(fieldKey);
  if (!meta?.conditionalOn) return true;

  const cond = meta.conditionalOn;
  const currentValue = form[cond.field];

  // Negative match (notIn): shown unless the value is in the excluded list.
  if ('notIn' in cond) {
    if (typeof currentValue !== 'string') return true; // unset → shown
    return !cond.notIn.includes(currentValue);
  }

  // Array-intersection match (includesAny): for multi_select controlling
  // fields. Shown when the referenced array has at least one value in
  // common with the trigger list. Used by step-12 link sub-steps which
  // gate on `familyMembers` (a multi-select) containing the matching
  // relation type. An empty / non-array referenced value → not shown.
  if ('includesAny' in cond) {
    if (!Array.isArray(currentValue)) return false;
    return currentValue.some(
      (v): v is string => typeof v === 'string' && cond.includesAny.includes(v),
    );
  }

  // Positive match (values): shown only when single value is in the list.
  if (typeof currentValue !== 'string') return false;
  return cond.values.includes(currentValue);
}

/**
 * Resolve a single-select field's current value to its display label.
 *
 * Uses getFieldOptions() from profile-fields for O(1) option lookup.
 * Falls back to the raw value if no option found.
 *
 * @param fieldKey - The field key (e.g., "gender", "familyReligion")
 * @param value - The current value (e.g., "man", "hindu")
 * @returns Display label string, or undefined if no value
 */
export function labelFor(fieldKey: string, value: unknown): string | undefined {
  if (!value || typeof value !== 'string') return undefined;
  const options = getFieldOptions(fieldKey);
  const opt = options.find((o: FieldOption) => o.value === value);
  return opt ? opt.label : value;
}

/**
 * Resolve a multi-select field's current values to display labels.
 *
 * @param fieldKey - The field key
 * @param values - Array of selected values
 * @returns Array of display labels, or undefined if empty
 */
export function labelsFor(fieldKey: string, values: unknown): string[] | undefined {
  if (!Array.isArray(values) || values.length === 0) return undefined;
  const options = getFieldOptions(fieldKey);
  return values.map((v: string) => {
    const opt = options.find((o: FieldOption) => o.value === v);
    return opt ? opt.label : v;
  });
}
